// ---------------------------------------------------------------------------
// Botão redondo da carteira: aparece no canto inferior direito SÓ no dia de um
// evento em que este aparelho tem fichas/ingressos para usar. Ao abrir o app e
// ao voltar para ele (no máximo a cada 5 min) pergunta ao banco o que há hoje.
// Esconde nas telas de pagamento, caixa, portaria e barraca. Não cria nada no
// banco — só lê (rpc fichas_de_hoje).
// ---------------------------------------------------------------------------
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import Sheet from '@/components/ui/Sheet';

interface FichaHoje {
  carteira_codigo: string;
  evento_id: number;
  evento_nome: string;
  fichas: number;
  ingressos: number;
}

const INTERVALO = 5 * 60 * 1000;
const LIMITE_CODIGOS = 20;

// Telas onde o botão NÃO aparece.
const ESCONDER: RegExp[] = [
  /^\/pedido\b/,
  /^\/ingresso\b/,
  /^\/inscricao\b/,
  /^\/minha-inscricao\b/,
  /^\/loja\/pedido\b/,
  /^\/loja\/barraca\b/,
  /^\/caixa\b/,
  /^\/portaria\b/,
  /^\/t\//,
  /^\/rifa\b/,
  /^\/sorteio\b/,
  /^\/presenca\b/,
  /^\/equipe\b/,
  /^\/servir\b/,
  /^\/plataforma\b/,
];

// Códigos de carteira guardados neste celular (chaves "carteira:<evento>").
function codigosDoAparelho(): string[] {
  const codigos: string[] = [];
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (k && k.startsWith('carteira:')) {
        const v = localStorage.getItem(k);
        if (v) codigos.push(v);
      }
    }
  } catch {
    /* armazenamento indisponível */
  }
  return Array.from(new Set(codigos)).slice(0, LIMITE_CODIGOS);
}

function jaPulsouHoje(): boolean {
  try {
    const hoje = new Intl.DateTimeFormat('pt-BR', { timeZone: 'America/Sao_Paulo' }).format(
      new Date(),
    );
    return localStorage.getItem('seliga:carteira_pulso') === hoje;
  } catch {
    return true;
  }
}

function marcarPulsoHoje(): void {
  try {
    const hoje = new Intl.DateTimeFormat('pt-BR', { timeZone: 'America/Sao_Paulo' }).format(
      new Date(),
    );
    localStorage.setItem('seliga:carteira_pulso', hoje);
  } catch {
    /* ignora */
  }
}

export default function BotaoCarteira() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [fichas, setFichas] = useState<FichaHoje[]>([]);
  const [escolher, setEscolher] = useState(false);
  const [pulsar, setPulsar] = useState(false);
  const ultimo = useRef(0);

  const escondido = ESCONDER.some((r) => r.test(pathname));

  const carregar = useCallback(async () => {
    const codigos = codigosDoAparelho();
    try {
      const { data, error } = await supabase.rpc('fichas_de_hoje', { p_codigos: codigos });
      if (error) return;
      const lista = (Array.isArray(data) ? data : []) as FichaHoje[];
      setFichas(lista.filter((f) => f && (f.fichas > 0 || f.ingressos > 0)));
    } catch {
      /* silencioso: o botão só não aparece */
    }
  }, []);

  // Abre consultando; depois, no máximo a cada 5 min ao voltar para o app.
  useEffect(() => {
    ultimo.current = Date.now();
    void carregar();
  }, [carregar]);

  useEffect(() => {
    const aoVoltar = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - ultimo.current < INTERVALO) return;
      ultimo.current = Date.now();
      void carregar();
    };
    document.addEventListener('visibilitychange', aoVoltar);
    return () => document.removeEventListener('visibilitychange', aoVoltar);
  }, [carregar]);

  // Pulso leve na primeira vez que aparece no dia.
  useEffect(() => {
    if (fichas.length === 0) return;
    if (jaPulsouHoje()) return;
    marcarPulsoHoje();
    setPulsar(true);
    const t = window.setTimeout(() => setPulsar(false), 700);
    return () => window.clearTimeout(t);
  }, [fichas]);

  const totalFichas = useMemo(
    () => fichas.reduce((soma, f) => soma + (f.fichas || 0), 0),
    [fichas],
  );

  if (escondido || fichas.length === 0) return null;

  const abrir = () => {
    if (fichas.length === 1) {
      navigate(`/carteira/${fichas[0].carteira_codigo}`);
      return;
    }
    setEscolher(true);
  };

  return (
    <>
      <button
        type="button"
        onClick={abrir}
        aria-label="Minhas fichas de hoje"
        className={`fixed right-4 z-40 flex h-[60px] w-[60px] cursor-pointer items-center justify-center rounded-full text-white shadow-[0_10px_24px_rgba(29,78,216,0.35)] transition-transform active:scale-95 ${
          pulsar ? 'animate-pulso' : ''
        }`}
        style={{
          background: '#1D4ED8',
          bottom: 'calc(env(safe-area-inset-bottom) + 16px)',
        }}
      >
        <i
          className={`${totalFichas > 0 ? 'ri-ticket-line' : 'ri-ticket-2-line'} text-[26px]`}
          aria-hidden
        />
        {totalFichas > 0 && (
          <span className="absolute -right-1 -top-1 flex h-6 min-w-6 items-center justify-center rounded-full bg-accent-500 px-1.5 text-[12px] font-extrabold text-white ring-2 ring-background-100">
            {totalFichas}
          </span>
        )}
      </button>

      <Sheet aberto={escolher} titulo="Suas fichas de hoje" aoFechar={() => setEscolher(false)}>
        <div className="space-y-2">
          {fichas.map((f) => (
            <button
              key={f.carteira_codigo}
              type="button"
              onClick={() => {
                setEscolher(false);
                navigate(`/carteira/${f.carteira_codigo}`);
              }}
              className="aperta flex w-full cursor-pointer items-center gap-3 rounded-[16px] bg-background-100 px-4 py-3.5 text-left transition-colors hover:bg-background-200/70"
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-500 text-white">
                <i className="ri-ticket-line text-[20px]" aria-hidden />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[15px] font-bold text-foreground-950">
                  {f.evento_nome}
                </span>
                <span className="block text-[13px] text-foreground-600">
                  {f.fichas > 0 ? `${f.fichas} ${f.fichas === 1 ? 'ficha' : 'fichas'}` : ''}
                  {f.fichas > 0 && f.ingressos > 0 ? ' · ' : ''}
                  {f.ingressos > 0
                    ? `${f.ingressos} ${f.ingressos === 1 ? 'ingresso' : 'ingressos'}`
                    : ''}
                </span>
              </span>
              <i className="ri-arrow-right-s-line text-xl text-foreground-400" aria-hidden />
            </button>
          ))}
        </div>
      </Sheet>
    </>
  );
}